import React from 'react';
import { useIntl, MessageDescriptor } from 'react-intl';
import { Col, FormFeedback, FormGroup, FormText, Input, Label, Row } from 'reactstrap';
import type { ProductReviewRole } from '../../../../../music24-api/src/productReviews/productReviewTypes';
import { TypedMemo } from '../../common/types';
import { reviewMessages } from './reviewMessages';
import { problemOf, REVIEW_LIMITS, REVIEW_ROLES, ReviewDraft, ReviewProblem } from './reviewRules';

const ROLE_LABELS: Record<ProductReviewRole, MessageDescriptor> = {
  artist: reviewMessages.roleArtist,
  manager: reviewMessages.roleManager,
  label: reviewMessages.roleLabelCompany,
  other: reviewMessages.roleOther,
};

interface ReviewIdentityFieldsProps {
  draft: ReviewDraft;
  onChange: (patch: Partial<ReviewDraft>) => void;
  problems: ReviewProblem[];
  disabled: boolean;
}

/** The name (prefilled) and the role of the author (design review-form a1). */
const ReviewIdentityFieldsComponent: React.FC<ReviewIdentityFieldsProps> = ({
  draft,
  onChange,
  problems,
  disabled,
}) => {
  const intl = useIntl();
  const nameProblem = problemOf(problems, 'authorName');

  return (
    <Row className="g-3">
      <Col sm={6}>
        <FormGroup className="mb-0">
          <Label for="reviewAuthorName">{intl.formatMessage(reviewMessages.nameLabel)}</Label>
          <Input
            id="reviewAuthorName"
            value={draft.authorName}
            maxLength={REVIEW_LIMITS.authorNameMax}
            invalid={nameProblem !== undefined}
            disabled={disabled}
            autoComplete="name"
            data-clarity-mask="true"
            onChange={(event) => onChange({ authorName: event.target.value })}
          />
          {nameProblem !== undefined ? (
            <FormFeedback>
              {nameProblem.kind === 'missing'
                ? intl.formatMessage(reviewMessages.nameMissing)
                : intl.formatMessage(reviewMessages.nameTooLong, { max: REVIEW_LIMITS.authorNameMax })}
            </FormFeedback>
          ) : (
            <FormText>{intl.formatMessage(reviewMessages.nameHint)}</FormText>
          )}
        </FormGroup>
      </Col>
      <Col sm={6}>
        <FormGroup className="mb-0">
          <Label for="reviewAuthorRole">{intl.formatMessage(reviewMessages.roleLabel)}</Label>
          <Input
            id="reviewAuthorRole"
            type="select"
            value={draft.authorRole}
            disabled={disabled}
            onChange={(event) => onChange({ authorRole: event.target.value as ProductReviewRole })}
          >
            {REVIEW_ROLES.map((role) => (
              <option key={role} value={role}>
                {intl.formatMessage(ROLE_LABELS[role])}
              </option>
            ))}
          </Input>
        </FormGroup>
      </Col>
    </Row>
  );
};

export const ReviewIdentityFields = TypedMemo(ReviewIdentityFieldsComponent);
